// @ts-check
/**
 * The Folder contract — what this package needs of whatever holds its bytes.
 *
 * A pack is written to and read from a *folder*, and the package must be able
 * to say what a folder is without reaching outside itself. So the contract
 * lives here, with the code that depends on it; the providers that answer it —
 * a directory handle, one blob, a directory on disk, a local process, an
 * object store — belong to whoever brings them, and the storage layer that
 * collects them (`helpers/storage.js`) re-exports what is defined here rather
 * than defining it.
 *
 * Every provider answers the same seven questions:
 *
 * ```text
 * list(at)  stat(at)  readFile(at, range?)  writeFile(at, bytes)
 * makeDirectory(at)   remove(at, options)   move(from, to)
 * ```
 *
 * **Paths** are `/`-separated and relative to the folder. Empty segments and
 * `.` are dropped; `..` is refused — a folder is a boundary, and nothing
 * reaches above it. `''` is the folder itself.
 *
 * **Ranges** are inclusive at both ends, as an HTTP `Range` is, so the same
 * arguments serve a handle, a file and a server.
 *
 * **Absence is one error.** Whatever the medium says when a path is not there
 * — a `DOMException`, an `ENOENT`, a 404 — the caller sees an error whose
 * `name` is `NotFoundError`, and nothing else is ever reported that way.
 * `isNotFound(error)` is the one question to ask.
 *
 * **Conformance is a test, not a promise.** `./conformance.js` runs the same
 * cases over every provider, so "behaves like the others" is checked rather
 * than assumed.
 *
 * **Two capabilities are OPTIONAL**, because only some media can offer them
 * and the layer that moves bytes must be able to ask rather than assume
 * (`./io.js`). Neither is part of the seven calls and `assertFolder` requires
 * neither:
 *
 * | Optional call | Answers with | Offered by |
 * | --- | --- | --- |
 * | `fileOf(at)` | a reference to the stored bytes — a `File` on the browser's own storage, a `Blob` over a range elsewhere. Taking it reads nothing | a medium that can hand out a reference |
 * | `seekableSink(at)` | a `SeekableSink`: write at an offset, truncate, size, close — what the append protocol needs | a medium that can be written anywhere but the end |
 *
 * A folder that offers neither is still a folder; it is read whole and written
 * whole, and `io.js` says so rather than failing.
 *
 * @module @smotaal/pack-fs/helpers
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Fable 5.1, Claude Opus 5 (code generation)
 */

/**
 * @typedef {{ name: string, kind: 'file' | 'directory', size: number, modifiedAt: number }} Entry
 *   A directory holds no size and no time of its own: both are `0`.
 */

/** @typedef {{ kind: 'file' | 'directory', size: number, modifiedAt: number }} Stat */

/** @typedef {{ start?: number, end?: number }} ByteRange `end` is INCLUSIVE. */

/**
 * The seven calls every folder answers, and the two a folder MAY.
 *
 * @typedef {{
 *   readonly name: string,
 *   list(at?: string): Promise<Entry[]>,
 *   stat(at: string): Promise<Stat>,
 *   readFile(at: string, range?: ByteRange): Promise<Uint8Array>,
 *   writeFile(at: string, bytes: Uint8Array | Blob | ArrayBuffer): Promise<Stat>,
 *   makeDirectory(at: string): Promise<Stat>,
 *   remove(at: string, options?: { recursive?: boolean }): Promise<{ removed: string }>,
 *   move(from: string, to: string): Promise<Stat>,
 *   fileOf?(at: string): Blob | Promise<Blob>,
 *   seekableSink?(at: string): SeekableSink | Promise<SeekableSink>,
 *   readonly moveKind?: MoveKind,
 * }} Folder
 */

/**
 * How a medium moves a path onto another: in one operation, or by copying the
 * bytes and removing the source. A folder that does not say is taken to copy,
 * which is the honest assumption — a move is at least that.
 *
 * @typedef {'rename' | 'copy'} MoveKind
 */

/**
 * How a sink reaches an offset. The sink says which, so a caller can record
 * the choice rather than guess it, and so nothing has to name a medium to
 * find out (`io.js`, X1).
 *
 * | Kind | The medium underneath |
 * | --- | --- |
 * | `sync` | an exclusive handle over a file, written at an offset directly |
 * | `positional` | a stream that takes a position with each write |
 * | `file` | a file descriptor written at an offset |
 * | `memory` | bytes held in memory |
 *
 * @typedef {'sync' | 'positional' | 'file' | 'memory'} SeekableSinkKind
 */

/**
 * A sink that writes at an offset — what the append protocol needs of a
 * medium, described structurally so the contract names no medium.
 *
 * Every call may answer with a promise, because opening a stream and writing
 * through one are asynchronous on some media and synchronous on others; a
 * caller awaits either way. `kind` is optional and is diagnostic only —
 * nothing in the format decides behaviour from it.
 *
 * @typedef {{
 *   write(bytes: Uint8Array, at: number): void | Promise<void>,
 *   truncate(size: number): void | Promise<void>,
 *   size(): number | Promise<number>,
 *   close?(): void | Promise<void>,
 *   readonly kind?: SeekableSinkKind,
 * }} SeekableSink
 */

/** The seven calls, by name. */
export const FOLDER_CALLS = Object.freeze([
    'list', 'stat', 'readFile', 'writeFile', 'makeDirectory', 'remove', 'move',
]);

/** @param {unknown} error */
export const isNotFound = error => /** @type {any} */ (error)?.name === 'NotFoundError';

/**
 * A path's segments: `/`-separated, empty and `.` dropped, `..` refused.
 *
 * @param {string} at
 * @returns {string[]}
 */
export function segmentsOf(at) {
    const parts = `${at ?? ''}`.split('/').filter(part => part && part !== '.');
    if (parts.includes('..')) throw new TypeError(`A folder path never climbs — “${at}”`);
    return parts;
}

/**
 * Joins path pieces, dropping empties — so `joinPath('', 'a')` is `a` and
 * `joinPath('a', '', 'b/c')` is `a/b/c`.
 *
 * @param {...(string | null | undefined)} parts
 */
export function joinPath(...parts) {
    return parts.flatMap(part => segmentsOf(part ?? '')).join('/');
}

/**
 * Refuses anything that does not answer the seven calls.
 *
 * @param {any} folder
 * @returns {Folder}
 */
export function assertFolder(folder) {
    for (const call of FOLDER_CALLS) {
        if (typeof folder?.[call] !== 'function') {
            throw new TypeError(`Not a folder — it has no ${call}()`);
        }
    }
    return folder;
}
